/**
 * MapSplitView.jsx · v9.4.1
 * Split View: Mapa interactivo (70%) + Agenda lateral (30%)
 * Reactivo a filtros globales. Dibuja ruta de atención.
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapContainer, TileLayer, Marker, Popup, Polyline, useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  MapPin, Clock, User, ChevronRight, Navigation, ExternalLink,
  CalendarClock, CheckCircle2, AlertTriangle, Cpu, Router,
  FileText, Loader2, Route, ListOrdered,
} from "lucide-react";

const LANE_COLORS = {
  pending: "#94A3B8",
  manual_assignment_required: "#D97706",
  assigned: "#2563EB",
  in_progress: "#7C3AED",
  on_site: "#0891B2",
  completed: "#059669",
  cancelled: "#DC2626",
};

const STATUS_LABELS = {
  pending: "Pendiente",
  manual_assignment_required: "Revisión",
  assigned: "Asignada",
  in_progress: "En progreso",
  on_site: "En sitio",
  completed: "Completada",
  cancelled: "Cancelada",
};

const buildIcon = (color, label) =>
  L.divIcon({
    className: "",
    html: `<div style="position:relative">
      <div style="background:${color};color:#fff;width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)">
        <div style="transform:rotate(45deg);font-size:9px;font-weight:800">${label}</div>
      </div>
    </div>`,
    iconSize: [28, 38],
    iconAnchor: [14, 28],
    popupAnchor: [0, -26],
  });

const buildNumberedIcon = (color, num) =>
  L.divIcon({
    className: "",
    html: `<div style="position:relative">
      <div style="background:${color};color:#fff;width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:900;font-size:12px;border:2.5px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35)">${num}</div>
    </div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -18],
  });

function FitBounds({ points }) {
  const map = useMap();
  const prevLen = useRef(0);
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === prevLen.current) return;
    prevLen.current = points.length;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lon], 14);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lon]));
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
  }, [points, map]);
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lon], 16, { duration: 0.5 });
  }, [target, map]);
  return null;
}

function fmtEc(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-EC", { timeZone: "America/Guayaquil", day: "2-digit", month: "short" }) +
      " " + d.toLocaleTimeString("es-EC", { timeZone: "America/Guayaquil", hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return null; }
}

export default function MapSplitView({ items = [], loading = false, onRefresh }) {
  const navigate = useNavigate();
  const [focusItem, setFocusItem] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);

  // Solo OTs asignadas con technician_id, scheduled_at y internal_status === "assigned" + GPS válido
  const gpsItems = useMemo(() =>
    items.filter((it) =>
      it.partner_lat && it.partner_lon && it.partner_lat !== 0 && it.partner_lon !== 0
      && it.technician_id
      && it.scheduled_at
      && it.internal_status === "assigned"
    ).map((it) => ({ ...it, lat: Number(it.partner_lat), lon: Number(it.partner_lon) })),
    [items]
  );

  // Agenda: ordena por scheduled_at ASC, luego por odoo_task_id
  const agenda = useMemo(() =>
    [...gpsItems].sort((a, b) => {
      const sa = a.scheduled_at || "";
      const sb = b.scheduled_at || "";
      if (sa && sb) return sa.localeCompare(sb);
      if (sa) return -1;
      if (sb) return 1;
      return (a.odoo_task_id || 0) - (b.odoo_task_id || 0);
    }),
    [gpsItems]
  );

  // Polyline: ruta de atención en orden de agenda
  const routePositions = useMemo(
    () => agenda.map((it) => [it.lat, it.lon]),
    [agenda]
  );

  const noGpsCount = items.filter((it) => it.technician_id && it.scheduled_at && it.internal_status === "assigned").length - gpsItems.length;

  return (
    <div className="flex h-[calc(100vh-230px)] min-h-[500px] rounded-xl overflow-hidden border border-gray-200 shadow-sm relative z-0" data-testid="map-split-view">
      {/* ═══ MAPA 70% ═══ */}
      <div className="w-[70%] relative z-0" data-testid="map-panel">
        {loading && (
          <div className="absolute inset-0 z-[1000] bg-white/60 flex items-center justify-center">
            <Loader2 className="animate-spin text-[#0A3747]" size={28} />
          </div>
        )}
        <MapContainer
          center={[-1.8312, -78.1834]}
          zoom={7}
          style={{ height: "100%", width: "100%" }}
          zoomControl={true}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          <FitBounds points={gpsItems} />
          {focusItem && <FlyTo target={focusItem} />}

          {/* Route Polyline */}
          {routePositions.length > 1 && (
            <Polyline
              positions={routePositions}
              pathOptions={{
                color: "#0A3747",
                weight: 3,
                opacity: 0.6,
                dashArray: "8, 6",
              }}
            />
          )}

          {/* Markers */}
          {agenda.map((it, idx) => {
            const color = LANE_COLORS[it.internal_status] || "#94A3B8";
            const isSelected = selectedIdx === idx;
            return (
              <Marker
                key={it.odoo_task_id}
                position={[it.lat, it.lon]}
                icon={buildNumberedIcon(isSelected ? "#E0E621" : color, idx + 1)}
                eventHandlers={{
                  click: () => { setSelectedIdx(idx); setFocusItem(null); },
                }}
              >
                <Popup maxWidth={280} className="map-popup-custom">
                  <div className="space-y-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-[#0A3747]">{it.afc_code}</span>
                      <span
                        className="px-1.5 py-0.5 rounded text-[9px] font-bold text-white"
                        style={{ background: color }}
                      >
                        {STATUS_LABELS[it.internal_status] || it.internal_status}
                      </span>
                    </div>
                    <div className="font-semibold text-gray-800">{it.partner_name || it.client_name || "—"}</div>
                    {it.address_full && <div className="text-gray-500 text-[10px]">{it.address_full}</div>}
                    {it.technician_name && (
                      <div className="flex items-center gap-1 text-[10px] text-blue-700">
                        <User size={9} /> {it.technician_name}
                      </div>
                    )}
                    {it.scheduled_at && (
                      <div className="flex items-center gap-1 text-[10px] text-emerald-700">
                        <CalendarClock size={9} /> {fmtEc(it.scheduled_at)}
                      </div>
                    )}
                    <div className="flex gap-1.5 pt-1">
                      <button
                        onClick={() => navigate(`/ot-smart/${it.odoo_task_id}`)}
                        className="flex-1 text-center text-[10px] font-bold px-2 py-1 rounded bg-[#0A3747] text-white hover:bg-[#0A3747]/90"
                      >
                        Abrir OT
                      </button>
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${it.lat},${it.lon}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] font-bold px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-0.5"
                      >
                        <Navigation size={9} /> Ruta
                      </a>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        {/* Overlay stats */}
        <div className="absolute top-3 left-14 z-[1000] flex items-center gap-1.5" data-testid="map-overlay-stats">
          <div className="bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-md border border-gray-200 flex items-center gap-2 text-[10px] font-bold">
            <MapPin size={12} className="text-[#0A3747]" />
            <span className="text-[#0A3747]">{gpsItems.length} en mapa</span>
            {noGpsCount > 0 && <span className="text-red-500">({noGpsCount} sin GPS)</span>}
          </div>
          {routePositions.length > 1 && (
            <div className="bg-[#0A3747]/90 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-md flex items-center gap-1.5 text-[10px] font-bold text-white">
              <Route size={11} />
              <span>{routePositions.length} paradas</span>
            </div>
          )}
        </div>
      </div>

      {/* ═══ AGENDA 30% ═══ */}
      <div className="w-[30%] bg-white border-l border-gray-200 flex flex-col" data-testid="agenda-panel">
        {/* Header */}
        <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#0A3747] to-[#0A3747]/90">
          <div className="flex items-center gap-2">
            <ListOrdered size={14} className="text-[#E0E621]" />
            <span className="text-xs font-bold text-white">Agenda de Atención</span>
          </div>
          <span className="text-[10px] font-mono text-[#E0E621] font-bold">{agenda.length} OTs</span>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-gray-400" size={20} />
            </div>
          ) : agenda.length === 0 ? (
            <div className="text-center py-16 px-4">
              <MapPin size={28} className="mx-auto text-gray-300 mb-2" />
              <div className="text-xs font-semibold text-gray-500">Sin OTs asignadas con GPS</div>
              <div className="text-[10px] text-gray-400 mt-1">Asigna un técnico o ajusta los filtros globales</div>
            </div>
          ) : (
            agenda.map((it, idx) => {
              const color = LANE_COLORS[it.internal_status] || "#94A3B8";
              const isActive = selectedIdx === idx;
              return (
                <div
                  key={it.odoo_task_id}
                  className={`px-3 py-2 border-b border-gray-50 cursor-pointer transition-colors ${
                    isActive ? "bg-[#FBFCE0] border-l-[3px]" : "hover:bg-gray-50 border-l-[3px] border-l-transparent"
                  }`}
                  style={isActive ? { borderLeftColor: color } : undefined}
                  onClick={() => {
                    setSelectedIdx(idx);
                    setFocusItem({ lat: it.lat, lon: it.lon });
                  }}
                  data-testid={`agenda-item-${it.odoo_task_id}`}
                >
                  <div className="flex items-start gap-2">
                    {/* Number badge */}
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-black text-white mt-0.5"
                      style={{ background: color }}
                    >
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-[10px] font-mono font-bold text-[#0A3747] truncate">
                          {it.afc_code}
                        </span>
                        <span
                          className="text-[8px] px-1.5 py-0.5 rounded font-bold text-white flex-shrink-0"
                          style={{ background: color }}
                        >
                          {STATUS_LABELS[it.internal_status] || it.internal_status}
                        </span>
                      </div>
                      <div className="text-[10px] font-medium text-gray-700 truncate mt-0.5">
                        {it.partner_name || it.client_name || "—"}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-[9px] text-gray-400">
                        {it.technician_name && (
                          <span className="flex items-center gap-0.5 truncate max-w-[100px]">
                            <User size={8} /> {it.technician_name}
                          </span>
                        )}
                        {it.scheduled_at && (
                          <span className="flex items-center gap-0.5 text-blue-500 font-semibold">
                            <CalendarClock size={8} /> {fmtEc(it.scheduled_at)}
                          </span>
                        )}
                      </div>
                      {it.solution_type_name || it.plan_name ? (
                        <div className="text-[9px] text-gray-400 truncate mt-0.5">
                          {it.solution_type_name || it.plan_name}
                        </div>
                      ) : null}
                    </div>
                    <ChevronRight size={12} className="text-gray-300 flex-shrink-0 mt-1" />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer stats */}
        <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-[9px] text-gray-500">
          <span>{agenda.length} asignadas y agendadas</span>
          <span>{noGpsCount > 0 ? `${noGpsCount} sin GPS` : "Todas con GPS"}</span>
        </div>
      </div>
    </div>
  );
}
